import * as THREE from 'three';
import stampUrl from '../../../Assets/Images/logo-stamp.png';

/**
 * The SamsonArt stamp as a coin, parked in the top-left of the frame and turning slowly.
 *
 * Built here rather than loaded as a GLB. There was no model to reuse — the only `logo.glb` in
 * the repo is a Blender tutorial placeholder (three untextured primitives and a camera, named
 * `CUBezierCurve.000`), and nothing referenced it. The mark itself is flat artwork, so the
 * extruded part is just a disc with a thickness: turning a plate of revolution into a model
 * file would add a binary asset, a loader and an async wait to say what two lines of
 * `CylinderGeometry` already say.
 *
 * The artwork comes from `Assets/Images/logo-stamp.png`, which is the gallery's
 * `images/logo-stamp.png` cropped to its alpha bounding box. The source is 1600x2000 and the
 * mark occupies 49.4% x 39.5% of that, sitting 30px off centre — so mapping it onto a coin
 * unchanged gives a small badge that wobbles as it turns. Cropping was done once, offline,
 * instead of papering over it with `repeat`/`offset` UV arithmetic that would have to be kept
 * in step with the source file.
 *
 * Nothing here is lit. The scene is a dark lobby and the coin is a fixed piece of furniture, so
 * `MeshBasicMaterial` on all three meshes keeps it readable at the same brightness whatever the
 * spotlight is doing. That also sidesteps the postprocessing chain — `process()` in MainScene is
 * commented out today, but a HUD that does not want a bokeh blur over it is better off not
 * being a surface that can receive one.
 */

/* ---- The numbers worth turning ---- */

/** Radius the coin is built at. Only a shape ratio — the group carries the real size. */
const COIN_R = 1;
/** Thickness as a fraction of the radius. */
const COIN_T = 0.16;
/** Segments. Enough that the rim reads as a circle while it turns, cheap enough to be free. */
const COIN_SEG = 96;

/** Where the coin sits in front of the camera, in world units. */
const DEPTH = 4;

/**
 * Coin radius as a fraction of the view's half-height, and the gap from the corner in the same
 * units. Both against the half-height rather than the width, so the coin holds its share of the
 * *screen* as the window is resized and as the viewport aspect changes — a portrait phone then
 * gets the same badge, in the same relation to the frame, as a desktop.
 */
const SIZE = 0.085;
const MARGIN = 0.055;

/**
 * Radians per second, and the sign is the whole of "counter-clockwise".
 *
 * Counter-clockwise about +Y is the right-hand rule: thumb up the axis, fingers curl positive,
 * so it is the direction a turn with the angle *increasing* goes when it is looked at from above.
 * That is also what it looks like from the front, where the coin's face swings towards the
 * viewer's right and its left edge comes round towards them. Accumulating downwards instead is
 * the mirror of that, and is the one-line change if this is the wrong way round; nothing else
 * here depends on the sign.
 *
 * 0.5 rad/s is about 12.6 seconds a turn — slow enough to read as drift rather than as a spinner.
 */
const SPIN = 0.5;

/**
 * The layer the coin lives on, out of every raycast's reach.
 *
 * `raycastFromCentre` is handed `scene.children` and walks it recursively, so anything added to
 * the scene is a target whether it wants to be one or not — the lobby's own reticle aims with
 * that call, and a coin in the corner would answer it. This is the same device the planet labels
 * use on layer 5 (Initial3DSetup, Planets.js): every raycaster is pinned to layer 0, so a mesh on
 * another layer is never a candidate. The other half of the trade is `camera.layers.enable(6)`
 * in Initial3DSetup — the camera's mask is what the renderer draws, so a layer the camera does
 * not carry is culled before any of this is looked at.
 *
 * Exported because the coin is also the handle for the category row beside it: `NavBar` raycasts
 * for a press on the mark, and it has to have the coin's layer to mask its own raycaster with.
 */
export const LOGO_LAYER = 6;

/** Scratch, at module scope because this is per-frame and the file beside it counts allocs. */
const cameraSpin = new THREE.Quaternion();
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const RIGHT = new THREE.Vector3();
const UP = new THREE.Vector3();
const FORWARD = new THREE.Vector3();
const clock = new THREE.Clock();
let spinAngle = 0;

export function createCornerLogo() {
    const group = new THREE.Group();
    group.name = 'cornerLogo';

    const texture = new THREE.TextureLoader().load(stampUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    // The coin turns, so most of the time its face is oblique and the artwork is being squashed
    // across many screen pixels. Without this the type smears as it passes the sides.
    texture.anisotropy = 4;

    // The body. A cylinder's axis is +Y on the way out of the constructor, and the coin turns
    // about Y, so the geometry is laid down on Z instead and the disc faces sideways — which
    // also puts the stamp discs on ±Z, where a circle's own UVs already point.
    const body = new THREE.Mesh(
        new THREE.CylinderGeometry(COIN_R, COIN_R, COIN_T, COIN_SEG),
        new THREE.MeshBasicMaterial({ color: 0x1b1620 })
    );
    body.geometry.rotateX(Math.PI / 2);
    group.add(body);

    // Two faces, not one. A single disc leaves the back of the coin a featureless dark plate,
    // and at half a turn that is all the camera sees — the mark would blink out and back twice a
    // revolution. The back is the same disc turned to face -Z, so the coin shows the stamp at
    // every angle; the artwork is mirror-imaged once a turn, which at 12.6 seconds and 8% of the
    // frame is not something the eye catches.
    const face = (z, flipped) => {
        const disc = new THREE.Mesh(
            new THREE.CircleGeometry(COIN_R, COIN_SEG),
            new THREE.MeshBasicMaterial({ map: texture, transparent: true })
        );
        disc.position.z = z;

        if (flipped) disc.rotation.y = Math.PI;

        group.add(disc);
    };

    // Just clear of the caps, so the transparent parts of the artwork show the dark body behind
    // them rather than fighting the cap for the same depth.
    face(COIN_T / 2 + 0.004, false);
    face(-COIN_T / 2 - 0.004, true);

    group.traverse(object => object.layers.set(LOGO_LAYER));

    return group;
}

/**
 * Puts the coin in the top-left corner of whatever camera is live, and turns it a little since
 * the last frame.
 *
 * Read from `getActiveCamera()` every frame rather than parented to the camera once, because
 * `setActiveCamera` can swap the active camera and a parented coin would travel with the one it
 * was given.
 *
 * The camera is read through its own `position` and `quaternion` rather than through
 * `getWorldPosition`/`getWorldQuaternion`, which is only sound because the rig it hangs from is
 * never given anything but `rig.position.set(0, 0, 0)` — no rotation, no scale — so the local
 * transform and the world one agree. If the rig ever gains either, these four reads have to
 * become the world-space calls `faceLabels` uses for the same reason. This file is a child of the
 * scene and not of the rig, so nothing else here inherits that arrangement.
 *
 * The spin is kept as an angle of its own and applied on top of the camera's orientation. The
 * obvious `group.rotation.y += spin` cannot be used — the first line of the placement overwrites
 * the whole quaternion with the camera's, which would throw the accumulated turn away — so the
 * two are composed instead: camera first, spin after, which turns the coin about its own axis as
 * the viewer sees it rather than about the world's.
 */
export function updateCornerLogo(logo, camera) {
    if (!logo || !camera) return;

    // Clamped: a hidden tab can leave a very large delta to make up, and a coin that jumps most
    // of a turn between two painted frames is a glitch, not a spin.
    const dt = Math.min(clock.getDelta(), 0.1);

    const halfH = DEPTH * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
    const halfW = halfH * camera.aspect;
    const radius = SIZE * halfH;
    const inset = MARGIN * halfH;

    logo.scale.setScalar(radius);

    // The camera's own axes, in the world. `forward` is -Z: a camera looks down its own negative
    // Z, which is the one sign in this file worth checking twice.
    RIGHT.set(1, 0, 0).applyQuaternion(camera.quaternion);
    UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
    FORWARD.set(0, 0, -1).applyQuaternion(camera.quaternion);

    logo.position.copy(camera.position)
        .addScaledVector(FORWARD, DEPTH)
        .addScaledVector(RIGHT, -(halfW - inset - radius))
        .addScaledVector(UP, halfH - inset - radius);

    spinAngle += SPIN * dt;

    logo.quaternion.copy(camera.quaternion)
        .multiply(cameraSpin.setFromAxisAngle(Y_AXIS, spinAngle));
}
